import type {Placement, Justify, PositionedPlacement} from 'src/types/position';


export type {Placement, Justify};

export function createElement(
  document: Document,
  type: string,
  props: {
    [key: string]: unknown;
  },
): HTMLElement {
  const el = document.createElement(type);
  return Object.assign(el, props);
}

export function pageHeight(): number {
  const {body, documentElement} = window.document;
  return Math.max(
    body.scrollHeight,
    body.clientHeight,
    documentElement.scrollHeight,
    documentElement.clientHeight,
  );
}

export function getFixedAnchorPosition(
  element: HTMLElement,
  placement: Placement = 'top',
  pad: number = 0,
  justify: Justify = 'center',
): PositionedPlacement {
  const rect = element.getBoundingClientRect();

  const docX = rect.left;
  const docY = rect.top;

  let x, y;

  switch (placement) {
    case 'bottom':
      y = docY + rect.height + pad;
      switch (justify) {
        case 'start':
          x = docX;
          break;
        case 'center':
          x = docX + rect.width / 2;
          break;
        case 'end':
        default:
          x = docX + rect.width;
          break;
      }
      break;
    case 'left':
      x = docX - pad;
      switch (justify) {
        case 'start':
          y = docY;
          break;
        case 'center':
          y = docY + rect.height / 2;
          break;
        case 'end':
        default:
          y = docY + rect.height;
          break;
      }
      break;
    case 'right':
      x = docX + rect.width + pad;
      y = docY + rect.height / 2;
      break;
    default:
      y = docY - pad;
      switch (justify) {
        case 'start':
          x = docX;
          break;
        case 'center':
          x = docX + rect.width / 2;
          break;
        case 'end':
        default:
          x = docX + rect.width;
          break;
      }
      break;
  }

  return {x, y, placement};
}

export function getAnchorPosition(
  element: HTMLElement,
  placement: Placement = 'top',
  pad: number = 0,
  justify: Justify = 'center',
): PositionedPlacement {
  const position = getFixedAnchorPosition(element, placement, pad, justify);
  const documentStyle = window.document.documentElement.style;
  return {
    ...position,
    x: position.x + window.pageXOffset - pxToNumber(documentStyle.paddingLeft),
    y: position.y + window.pageYOffset - pxToNumber(documentStyle.paddingTop),
  };
}

export function pxToNumber(px: string): number {
  return parseFloat(px.replace('px', '') || '0');
}

// TODO (kyle): add more handler types?
type Handlers = Partial<{
  // @ts-expect-error - TS2411 - Property 'click' of type '(arg1: MouseEvent) => unknown' is not assignable to 'string' index type '(arg1: Event) => unknown'.
  click: (arg1: MouseEvent) => unknown;
  // @ts-expect-error - TS2411 - Property 'mousedown' of type '(arg1: MouseEvent) => unknown' is not assignable to 'string' index type '(arg1: Event) => unknown'.
  mousedown: (arg1: MouseEvent) => unknown;
  // @ts-expect-error - TS2411 - Property 'mouseup' of type '(arg1: MouseEvent) => unknown' is not assignable to 'string' index type '(arg1: Event) => unknown'.
  mouseup: (arg1: MouseEvent) => unknown;
  // @ts-expect-error - TS2411 - Property 'pointerdown' of type '(arg1: PointerEvent) => unknown' is not assignable to 'string' index type '(arg1: Event) => unknown'.
  pointerdown: (arg1: PointerEvent) => unknown;
  // @ts-expect-error - TS2411 - Property 'pointerup' of type '(arg1: PointerEvent) => unknown' is not assignable to 'string' index type '(arg1: Event) => unknown'.
  pointerup: (arg1: PointerEvent) => unknown;
  // @ts-expect-error - TS2411 - Property 'pointercancel' of type '(arg1: PointerEvent) => unknown' is not assignable to 'string' index type '(arg1: Event) => unknown'.
  pointercancel: (arg1: PointerEvent) => unknown;
  // @ts-expect-error - TS2411 - Property 'openMessagingPanel' of type '(arg1: CustomEvent<any>) => unknown' is not assignable to 'string' index type '(arg1: Event) => unknown'.
  openMessagingPanel: (arg1: CustomEvent) => unknown;
  [key: string]: (arg1: Event) => unknown;
}>;
export function listen(
  target: EventTarget,
  handlers: Handlers,
  // @ts-expect-error TS2304
  options?: EventListenerOptionsOrUseCapture,
  hook: 'addEventListener' | 'removeEventListener' = 'addEventListener',
) {
  for (const eventName in handlers) {
    // @ts-expect-error - TS2345 - Argument of type '((arg1: Event) => unknown) | undefined' is not assignable to parameter of type 'EventListenerOrEventListenerObject | null'.
    target[hook](eventName, handlers[eventName], options);
  }
}

export function forget(
  target: EventTarget,
  events: Handlers,
  // @ts-expect-error TS2304
  options: EventListenerOptionsOrUseCapture,
): void {
  return listen(target, events, options, 'removeEventListener');
}

type MixedEvent = React.SyntheticEvent<EventTarget> | Event;
export function stopEvent(event: MixedEvent) {
  event.stopPropagation();
}
export function stopEventImmediately(event: React.SyntheticEvent<EventTarget>) {
  event.nativeEvent.stopImmediatePropagation();
}
export function cancelEvent(event: MixedEvent) {
  event.stopPropagation();
  event.preventDefault();
}

export function requestPointerLock(element: Element): Promise<void> {
  return new Promise(
    (
      resolve: (result: Promise<undefined> | undefined) => void,
      reject: (error?: any) => void,
    ) => {
      const handleChange = () => {
        if (document.pointerLockElement === element) {
          // @ts-expect-error - TS2794 - Expected 1 arguments, but got 0. Did you forget to include 'void' in your type argument to 'Promise'?
          resolve();
          removeHandlers();
        }
      };
      const handleError = () => {
        reject();
        removeHandlers();
      };
      const removeHandlers = () => {
        // @ts-expect-error - TS2554 - Expected 3 arguments, but got 2.
        forget(document, {
          pointerlockchange: handleChange,
          pointerlockerror: handleError,
        });
      };

      listen(document, {
        pointerlockchange: handleChange,
        pointerlockerror: handleError,
      });

      element.requestPointerLock();
    },
  );
}

export function checkDateInputSupport(): boolean {
  const input = window.document.createElement('input');
  input.setAttribute('type', 'date');

  const notADateValue = 'not-a-date';
  input.setAttribute('value', notADateValue);

  return input.value !== notADateValue;
}
//reference: https://stackoverflow.com/a/10199306

export const getListPasteHandler = ({
  listItemSeparatorRegex = /[\,\n]/,
  handleValue,
}: {
  listItemSeparatorRegex?: RegExp;
  handleValue?: (arg1: string[]) => unknown;
}): ((arg1: React.ClipboardEvent<Element>) => unknown) => {
  const handlePaste = (event: React.ClipboardEvent<Element>) => {
    const value = event.clipboardData?.getData('text');
    if (!value || !value.length) {
      return;
    }
    //do nothing if the copied string fails the regex test
    if (!listItemSeparatorRegex.test(value)) {
      return;
    }
    event.preventDefault();
    const parsedValues = value
      .split(listItemSeparatorRegex)
      .reduce<Array<any>>((acc, val) => {
        const newVal = val.trim();
        if (
          // value exists
          !!newVal.length &&
          // value not already in queue
          !acc.includes(newVal)
        ) {
          acc.push(newVal);
        }
        return acc;
      }, []);
    handleValue?.(parsedValues);
  };

  return handlePaste;
};

export function hasSupportCSSStyleSheet(): boolean {
  if (!window.document.adoptedStyleSheets) {
    return false;
  }
  try {
    new CSSStyleSheet();
    return true;
  } catch (e: any) {
    return false;
  }
}
